<template>
  <div
    v-if="accountData.admin_account_id && localCompanyData"
    class="alert alert-subtle-warning p-4 admin-panel"
  >
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="mb-4">
            <h4 class="font-serif mb-0">
              <FontAwesomeIcon icon="user-shield" class="me-2" />
              Admin Panel
            </h4>
          </div>

          <!-- Registry Connection Management -->
          <div class="card mb-4">
            <div class="card-body">
              <RegistryConnectionManagementComponent
                :selected-company="selectedCompany"
              />
            </div>
          </div>

          <!-- Account Settings -->
          <div class="card mb-4">
            <div class="card-header bg-primary text-white">
              <h5 class="mb-0">
                <FontAwesomeIcon icon="user" class="me-2" />
                Account Settings
              </h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <!-- Account Registry Code -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Account Registry Code</label
                  >
                  <input
                    type="text"
                    v-model="localAccountData.registry_code"
                    class="form-control"
                    placeholder="Enter account registry code"
                  />
                </div>

                <!-- Account Has Registered -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Account Registered</label
                  >
                  <div class="d-flex gap-3 align-items-center h-100">
                    <div class="form-check form-switch">
                      <input
                        type="checkbox"
                        id="accountHasRegistered"
                        class="form-check-input"
                        v-model="localAccountData.has_registered"
                        role="switch"
                      />
                      <label
                        class="form-check-label"
                        for="accountHasRegistered"
                      >
                        {{ localAccountData.has_registered ? "Yes" : "No" }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Internal Role -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{
                    $t("dashboard.internal_role")
                  }}</label>
                  <select
                    :value="localAccountData.internal_role"
                    @change="updateInternalRole"
                    class="form-select"
                  >
                    <option value="user">{{ $t("dashboard.user") }}</option>
                    <option value="sales_admin">
                      {{ $t("dashboard.sales_admin") }}
                    </option>
                    <option value="super_admin">
                      {{ $t("dashboard.super_admin") }}
                    </option>
                  </select>
                </div>

                <!-- Comment -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{
                    $t("account.comment")
                  }}</label>
                  <input
                    type="text"
                    v-model="localAccountData.comment"
                    class="form-control"
                    placeholder="Internal notes"
                  />
                </div>

                <!-- Sale Notification Emails Unsubscribed -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Sale Notification Emails Unsubscribed</label
                  >
                  <div class="form-check form-switch">
                    <input
                      type="checkbox"
                      id="transactionEmailsUnsubscribed"
                      class="form-check-input"
                      v-model="localAccountData.transaction_emails_unsubscribed"
                      role="switch"
                    />
                    <label
                      class="form-check-label"
                      for="transactionEmailsUnsubscribed"
                    >
                      {{
                        localAccountData.transaction_emails_unsubscribed
                          ? "Yes"
                          : "No"
                      }}
                    </label>
                  </div>
                </div>

                <!-- Account Feature Flags -->
                <div class="col-12">
                  <label class="form-label fw-semibold mb-2"
                    >Account Feature Flags</label
                  >
                  <div class="row">
                    <div
                      v-for="(label, flagName) in ACCOUNT_FEATURE_FLAGS"
                      :key="`account-flag-${flagName}`"
                      class="col-md-4 mb-2"
                    >
                      <div class="form-check">
                        <input
                          type="checkbox"
                          :id="`account-flag-${flagName}`"
                          class="form-check-input"
                          :checked="
                            localAccountData.feature_flags?.[flagName] === true
                          "
                          @change="
                            updateAccountFeatureFlag(
                              flagName,
                              ($event.target as HTMLInputElement).checked
                            )
                          "
                        />
                        <label
                          class="form-check-label"
                          :for="`account-flag-${flagName}`"
                        >
                          {{ label }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Company Settings -->
          <div class="card mb-4">
            <div class="card-header bg-success text-white">
              <h5 class="mb-0">
                <FontAwesomeIcon icon="building" class="me-2" />
                Company Settings
              </h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <!-- Company Name -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Company Name</label>
                  <input
                    type="text"
                    v-model="localCompanyData.name"
                    class="form-control"
                    placeholder="Enter company name"
                  />
                </div>

                <!-- Company Country -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Company Country</label>
                  <select
                    v-model="localCompanyData.country"
                    class="form-select"
                  >
                    <option value="">Select Country</option>
                    <option
                      v-for="country in ALL_COUNTRIES"
                      :key="country.value"
                      :value="country.value"
                    >
                      {{ country.label }}
                    </option>
                  </select>
                </div>

                <!-- VAT -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{
                    $t("company.vat")
                  }}</label>
                  <input
                    type="text"
                    v-model="localCompanyData.vat"
                    class="form-control"
                    placeholder="VAT number"
                  />
                </div>

                <!-- Payout currency -->
                <div class="col-md-6">
                  <label
                    for="admin-company-payout-currency"
                    class="form-label fw-semibold"
                    >Payout currency</label
                  >
                  <select
                    id="admin-company-payout-currency"
                    v-model="localCompanyData.payout_currency"
                    data-test="admin-payout-currency"
                    class="form-select"
                  >
                    <option
                      v-for="currency in SUPPORTED_CURRENCIES"
                      :key="currency"
                      :value="currency"
                    >
                      {{ currency }}
                    </option>
                  </select>
                  <div class="form-text">
                    A currency other than EUR needs the Ledger Reads flag; the
                    server refuses it otherwise.
                  </div>
                </div>

                <!-- Company Registry Code -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Company Registry Code</label
                  >
                  <input
                    type="text"
                    v-model="localCompanyData.registry_code"
                    class="form-control"
                    placeholder="Enter company registry code"
                  />
                </div>

                <!-- Company Has Registered -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Company Registered</label
                  >
                  <div class="d-flex gap-3 align-items-center h-100">
                    <div class="form-check form-switch">
                      <input
                        type="checkbox"
                        id="companyHasRegistered"
                        class="form-check-input"
                        v-model="localCompanyData.has_registered"
                        role="switch"
                      />
                      <label
                        class="form-check-label"
                        for="companyHasRegistered"
                      >
                        {{ localCompanyData.has_registered ? "Yes" : "No" }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Fee Model -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{
                    $t("dashboard.fee_model")
                  }}</label>
                  <div class="d-flex align-items-center gap-2">
                    <select v-model="selectedFeeModel" class="form-select">
                      <option value="standard">
                        {{ $t("dashboard.standard") }}
                      </option>
                      <option value="special_free">
                        {{ $t("dashboard.special_free") }}
                      </option>
                      <option value="special_1eur_per_mw">
                        {{ $t("dashboard.special_1eur_per_mw") }}
                      </option>
                      <option value="custom_percentage">
                        {{ $t("dashboard.custom_percentage") }}
                      </option>
                    </select>
                    <input
                      v-if="selectedFeeModel === 'custom_percentage'"
                      type="number"
                      v-model.number="customPercentage"
                      class="form-control"
                      style="max-width: 100px"
                      min="1"
                      max="100"
                      step="1"
                      @input="
                        (e: Event) =>
                          updateCustomPercentage(
                            +(e.target as HTMLInputElement).value
                          )
                      "
                    />
                    <span v-if="selectedFeeModel === 'custom_percentage'"
                      >%</span
                    >
                  </div>
                </div>

                <!-- Registry Excluded Devices -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{
                    $t("dashboard.registry_excluded_devices")
                  }}</label>
                  <input
                    type="text"
                    :value="
                      localCompanyData.registry_excluded_devices?.join(',')
                    "
                    @input="updateRegistryExcludedDevices"
                    class="form-control"
                    placeholder="Comma-separated device IDs"
                  />
                </div>

                <!-- Allowed invite domains -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{
                    $t("dashboard.allowed_invite_domains_label")
                  }}</label>
                  <input
                    type="text"
                    :value="localCompanyData.allowed_invite_domains?.join(',')"
                    @input="updateAllowedInviteDomains"
                    class="form-control"
                    placeholder="example.org,example.com"
                  />
                  <div class="form-text">
                    {{ $t("dashboard.allowed_invite_domains_help") }}
                  </div>
                </div>

                <!-- NDA Signed Link -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{
                    $t("dashboard.nda_signed_link")
                  }}</label>
                  <input
                    type="text"
                    v-model="localCompanyData.nda_signed_link"
                    class="form-control"
                    placeholder="NDA document link"
                  />
                </div>

                <!-- Membership Agreement Signed At -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Membership Agreement Signed</label
                  >
                  <input
                    type="datetime-local"
                    class="form-control"
                    v-model="membershipAgreementDate"
                  />
                </div>

                <!-- Retention Notification Silenced -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Retention Notification Silenced</label
                  >
                  <div class="form-check form-switch">
                    <input
                      type="checkbox"
                      id="retentionSilenced"
                      class="form-check-input"
                      v-model="localCompanyData.retention_notification_silenced"
                      role="switch"
                    />
                    <label class="form-check-label" for="retentionSilenced">
                      {{
                        localCompanyData.retention_notification_silenced
                          ? "Yes"
                          : "No"
                      }}
                    </label>
                  </div>
                </div>

                <!-- Monthly Notification Rollup -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold"
                    >Monthly Notification Rollup</label
                  >
                  <div class="form-check form-switch">
                    <input
                      type="checkbox"
                      id="monthlyRollup"
                      class="form-check-input"
                      v-model="localCompanyData.monthly_notification_rollup"
                      role="switch"
                    />
                    <label class="form-check-label" for="monthlyRollup">
                      {{
                        localCompanyData.monthly_notification_rollup
                          ? "Yes"
                          : "No"
                      }}
                    </label>
                  </div>
                </div>

                <!-- Forward Sales Enabled -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold mb-2"
                    >Forward Sales Enabled</label
                  >
                  <div class="d-flex gap-3">
                    <div class="form-check">
                      <input
                        type="radio"
                        id="forwardSalesYes"
                        :value="true"
                        class="form-check-input"
                        :checked="
                          localCompanyData.forward_sales_enabled === true
                        "
                        @change="updateForwardSalesEnabled(true)"
                      />
                      <label class="form-check-label" for="forwardSalesYes">{{
                        $t("common.yes")
                      }}</label>
                    </div>
                    <div class="form-check">
                      <input
                        type="radio"
                        id="forwardSalesNo"
                        :value="false"
                        class="form-check-input"
                        :checked="
                          localCompanyData.forward_sales_enabled === false
                        "
                        @change="updateForwardSalesEnabled(false)"
                      />
                      <label class="form-check-label" for="forwardSalesNo">{{
                        $t("common.no")
                      }}</label>
                    </div>
                  </div>
                </div>

                <!-- Is Test Company -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold mb-2">{{
                    $t("dashboard.is_test_company")
                  }}</label>
                  <div class="d-flex gap-3">
                    <div class="form-check">
                      <input
                        type="radio"
                        id="testYes"
                        :value="true"
                        class="form-check-input"
                        :checked="localCompanyData.is_test_company === true"
                        @change="updateIsTestCompany(true)"
                      />
                      <label class="form-check-label" for="testYes">{{
                        $t("common.yes")
                      }}</label>
                    </div>
                    <div class="form-check">
                      <input
                        type="radio"
                        id="testNo"
                        :value="false"
                        class="form-check-input"
                        :checked="localCompanyData.is_test_company === false"
                        @change="updateIsTestCompany(false)"
                      />
                      <label class="form-check-label" for="testNo">{{
                        $t("common.no")
                      }}</label>
                    </div>
                  </div>
                </div>

                <!-- Company Feature Flags -->
                <div class="col-12">
                  <label class="form-label fw-semibold mb-2"
                    >Company Feature Flags</label
                  >
                  <div class="row">
                    <div
                      v-for="(label, flagName) in FEATURE_FLAGS"
                      :key="flagName"
                      class="col-md-4 col-lg-3 mb-2"
                    >
                      <div class="form-check">
                        <input
                          type="checkbox"
                          :id="`flag-${flagName}`"
                          class="form-check-input"
                          :checked="
                            localCompanyData.feature_flags?.[flagName] === true
                          "
                          @change="
                            updateFeatureFlag(
                              flagName,
                              ($event.target as HTMLInputElement).checked
                            )
                          "
                        />
                        <label
                          class="form-check-label"
                          :for="`flag-${flagName}`"
                        >
                          {{ label }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Hosted account regions (Receive GO) -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold mb-2">{{
                    $t("dashboard.hosted_account_regions_label")
                  }}</label>
                  <p class="text-muted small mb-2">
                    {{ $t("dashboard.hosted_account_regions_help") }}
                  </p>
                  <MultiSelectComponent
                    :options="hostedRegionOptions"
                    :modelValue="
                      localCompanyData.unlocked_hosted_account_regions ?? []
                    "
                    @update:modelValue="updateHostedRegions"
                    :isLoading="hostedRegionsLoading"
                    selectedFirst
                    :maxDisplayLabels="8"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <div class="text-center mb-4">
            <button
              class="btn btn-primary btn-lg px-5"
              @click="updateAccountData"
            >
              <FontAwesomeIcon icon="save" class="me-2" />
              {{ $t("dashboard.save_changes") }}
            </button>
          </div>

          <!-- Invoicing Config -->
          <div class="card mb-4">
            <div class="card-body">
              <FeeConfigManagementComponent :selectedCompany />
            </div>
          </div>

          <!-- Referral Settings -->
          <div class="card mb-4">
            <div class="card-body">
              <ReferralSettingsComponent :selectedCompany :accountData />
            </div>
          </div>

          <!-- Advanced Settings -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Advanced Settings</h5>
            </div>
            <div class="card-body">
              <!-- Custom contract upload -->
              <InfoCardComponent buttonText="Manage Contracts" class="mb-3">
                <template #title>
                  <h6 class="mb-0">Custom Contract Upload</h6>
                </template>
                <template #content>
                  <AdminContractUploadComponent
                    :accountData="accountData"
                    :companyIndex="companyIndex"
                  />
                </template>
              </InfoCardComponent>

              <!-- Tracking Information -->
              <InfoCardComponent buttonText="View Trackings" class="mb-3">
                <template #title>
                  <h6 class="mb-0">{{ $t("dashboard.all_trackings") }}</h6>
                </template>
                <template #content>
                  <AdminConversionTrackingsComponent
                    :trackings="accountData.trackings ?? []"
                  />
                </template>
              </InfoCardComponent>

              <AdminCompanyTransferComponent
                :company="selectedCompany"
                :companies="accountData.companies"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoCardComponent from "@/components/common/InfoCardComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useServerDraft } from "@/composables/useServerDraft";
import {
  ACCOUNT_FEATURE_FLAGS,
  ALL_COUNTRIES,
  FEATURE_FLAGS,
} from "@/constants";
import AdminCompanyTransferComponent from "@/dashboard/admin/AdminCompanyTransferComponent.vue";
import { SUPPORTED_CURRENCIES } from "@/money/currency_constants";
import AdminContractUploadComponent from "@/dashboard/admin/AdminContractUploadComponent.vue";
import AdminConversionTrackingsComponent from "@/dashboard/admin/AdminConversionTrackingsComponent.vue";
import FeeConfigManagementComponent from "@/dashboard/admin/FeeConfigManagementComponent.vue";
import ReferralSettingsComponent from "@/dashboard/admin/ReferralSettingsComponent.vue";
import RegistryConnectionManagementComponent from "@/dashboard/admin/RegistryConnectionManagementComponent.vue";
import { fetchSolderaRegistryAccounts } from "@/dashboard/inventory_management/common/registryApi";
import type { SolderaRegistryAccount } from "@/dashboard/inventory_management/company_orders/types/virtualAccountTypes";
import type { Account, Company } from "@/types";
import { format } from "date-fns";
import { pick } from "lodash-es";
import { computed, onMounted, ref } from "vue";

// The fields each form edits. The drafts hold only these, so a binding to any other field fails to
// type-check instead of rendering blank.
const ACCOUNT_FORM_FIELDS = [
  "id",
  "registry_code",
  "has_registered",
  "internal_role",
  "comment",
  "feature_flags",
  "transaction_emails_unsubscribed",
] as const satisfies readonly (keyof Account)[];
const COMPANY_FORM_FIELDS = [
  "id",
  "name",
  "country",
  "vat",
  "registry_code",
  "has_registered",
  "fee_model",
  "payout_currency",
  "registry_excluded_devices",
  "allowed_invite_domains",
  "nda_signed_link",
  "membership_agreement_signed_at",
  "retention_notification_silenced",
  "monthly_notification_rollup",
  "forward_sales_enabled",
  "is_test_company",
  "feature_flags",
  "unlocked_hosted_account_regions",
] as const satisfies readonly (keyof Company)[];

type AccountDraft = Pick<Account, (typeof ACCOUNT_FORM_FIELDS)[number]>;
// Empty while the account has no company.
type CompanyDraft = Partial<
  Pick<Company, (typeof COMPANY_FORM_FIELDS)[number]>
>;

const props = defineProps<{
  accountData: Account;
  companyIndex: number;
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const accountStore = useAccountStore();
const feeModelChoice = ref<string>("standard");
const customPercentage = ref<number>(5);
const solderaRegistryAccounts = ref<SolderaRegistryAccount[]>([]);
const hostedRegionsLoading = ref(false);

const selectedCompany = computed(
  () => props.accountData.companies[props.companyIndex]
);

const localAccountData = useServerDraft(
  () => pick(props.accountData, ACCOUNT_FORM_FIELDS),
  (account): AccountDraft => account
);

const localCompanyData = useServerDraft(
  () =>
    selectedCompany.value && pick(selectedCompany.value, COMPANY_FORM_FIELDS),
  (company): CompanyDraft =>
    company
      ? {
          ...company,
          membership_agreement_signed_at: company.membership_agreement_signed_at
            ? new Date(company.membership_agreement_signed_at).toISOString()
            : null,
        }
      : {},
  {
    onReset: (company) => {
      feeModelChoice.value = parseFeeModel(company?.fee_model ?? null);
    },
    immediate: true,
  }
);

// datetime-local shows and takes the admin's local wall-clock time; the draft holds the instant.
const membershipAgreementDate = computed({
  get: () => {
    const signedAt = localCompanyData.value.membership_agreement_signed_at;
    return signedAt ? format(signedAt, "yyyy-MM-dd'T'HH:mm") : "";
  },
  set: (local: string) => {
    localCompanyData.value.membership_agreement_signed_at = local
      ? new Date(local).toISOString()
      : null;
  },
});

const selectedFeeModel = computed({
  get: () => feeModelChoice.value,
  set: (value: string) => {
    feeModelChoice.value = value;
    updateFeeModel();
  },
});

const hostedRegionOptions = computed(() =>
  solderaRegistryAccounts.value.map((acc) => ({
    label: acc.country_code,
    value: acc.country_code,
  }))
);

onMounted(() => {
  void loadSolderaAccountsForAdmin();
});

function parseFeeModel(feeModel: string | null) {
  if (!feeModel) {
    return "standard";
  }
  const match = feeModel.match(/^special_(\d{1,2})pct$/);
  if (match) {
    customPercentage.value = parseInt(match[1], 10);
    return "custom_percentage";
  }
  return feeModel;
}

function updateFeeModel() {
  let newFeeModel: string;
  if (feeModelChoice.value === "custom_percentage") {
    newFeeModel = `special_${customPercentage.value}pct`;
  } else {
    newFeeModel = feeModelChoice.value;
  }
  localCompanyData.value = {
    ...localCompanyData.value,
    fee_model: newFeeModel,
  };
}

function updateCustomPercentage(value: number) {
  customPercentage.value = Math.min(100, Math.max(1, Math.round(value)));
  updateFeeModel();
}

function updateInternalRole(event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  localAccountData.value = {
    ...localAccountData.value,
    internal_role: value,
  };
}

function updateRegistryExcludedDevices(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  localCompanyData.value = {
    ...localCompanyData.value,
    registry_excluded_devices: value.split(","),
  };
}

// Kept as a raw comma split so typing a trailing comma survives the re-render; the backend
// trims, lowercases and drops blanks (app/company/invite_domain_policy.py).
function updateAllowedInviteDomains(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  localCompanyData.value = {
    ...localCompanyData.value,
    allowed_invite_domains: value.split(","),
  };
}

function updateIsTestCompany(value: boolean) {
  localCompanyData.value = {
    ...localCompanyData.value,
    is_test_company: value,
  };
}

function updateForwardSalesEnabled(value: boolean) {
  localCompanyData.value = {
    ...localCompanyData.value,
    forward_sales_enabled: value,
  };
}

function updateFeatureFlag(flagName: string, enabled: boolean) {
  localCompanyData.value = {
    ...localCompanyData.value,
    feature_flags: {
      ...(localCompanyData.value.feature_flags ?? {}),
      [flagName]: enabled,
    },
  };
}

function updateHostedRegions(regions: string[]) {
  localCompanyData.value = {
    ...localCompanyData.value,
    unlocked_hosted_account_regions: regions,
  };
}

async function loadSolderaAccountsForAdmin() {
  hostedRegionsLoading.value = true;
  try {
    const res = await fetchSolderaRegistryAccounts();
    solderaRegistryAccounts.value = res.accounts;
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    hostedRegionsLoading.value = false;
  }
}

function updateAccountFeatureFlag(flagName: string, enabled: boolean) {
  localAccountData.value = {
    ...localAccountData.value,
    feature_flags: {
      ...(localAccountData.value.feature_flags ?? {}),
      [flagName]: enabled,
    },
  };
}

async function updateAccountData() {
  try {
    const updatedSelectedCompany = {
      ...selectedCompany.value,
      ...localCompanyData.value,
    };

    // internal_role is a super-only privilege change handled by its own endpoint, not the
    // SALES_WRITE company-update payload. Send it separately (and first) when it changed.
    const roleChanged =
      localAccountData.value.internal_role !== props.accountData.internal_role;
    if (roleChanged && localAccountData.value.id) {
      await goSolidApi.put(
        `/api/admin/accounts/${localAccountData.value.id}/internal-role`,
        { internal_role: localAccountData.value.internal_role }
      );
    }

    // The company-update payload must not carry internal_role (the backend write model no
    // longer accepts it).
    const { internal_role: _omitInternalRole, ...accountWithoutRole } = {
      ...props.accountData,
      ...localAccountData.value,
    };
    const payload = {
      ...accountWithoutRole,
      companies: props.accountData.companies.map(
        (company: Company, index: number) => {
          if (index === props.companyIndex) {
            return updatedSelectedCompany;
          }
          return company;
        }
      ),
    };

    await accountStore.postAccountData(
      `/api/admin/company/${updatedSelectedCompany.id}`,
      payload
    );
    globalSuccessHandler("Success", "Account data updated successfully.");
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
}
</script>
